import React, { useEffect, useRef, useState } from "react";
import {
  loadSearchData,
  search,
  SearchResult,
  getRandomCysteine,
} from "../utils/searchDataLoader";
import "./GoogleHome.css";
import CysteineTable from "./CysteineTable";
import ProteinExplorer from "./ProteinExplorer";
import { useFeatureFlagEnabled, usePostHog } from "posthog-js/react";

type SearchSource = "search_submit" | "suggestion_click" | "feeling_lucky";

const GoogleHome: React.FC = () => {
  const posthog = usePostHog();
  const [query, setQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProtein, setSelectedProtein] = useState<string | null>(null);
  const [selectedResult, setSelectedResult] = useState<SearchResult | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Check if new protein explorer is enabled
  const isProteinExplorerEnabled = useFeatureFlagEnabled('protein-explorer-split-view');

  // Load data on mount
  useEffect(() => {
    loadSearchData()
      .then(() => setIsLoading(false))
      .catch((err) => {
        console.error("Failed to load search data:", err);
        setIsLoading(false);
      });
  }, []);

  // Handle query changes
  useEffect(() => {
    if (query.trim()) {
      const results = search(query, 'all');
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  }, [query]);

  const trackCysteineSelection = (
    source: SearchSource,
    result: SearchResult,
    inputQuery?: string
  ) => {
    if (!posthog) return;
    const searchQuery = (inputQuery ?? query).trim();
    const sessionURL = posthog.get_session_replay_url?.({ withTimestamp: true });
    posthog.capture("cysteine_search", {
      source,
      query: searchQuery || undefined,
      result_type: result.type,
      result_primary: result.primary,
      result_secondary: result.secondary,
      result_tertiary: result.tertiary,
      result_index: result.index,
      is_feeling_lucky: source === "feeling_lucky",
      sessionURL,
    });
  };

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (query.trim() && searchResults.length > 0) {
      // Use the first search result
      handleResultSelection(searchResults[0], "search_submit", query);
    }
  };
  
  const handleFeelingLucky = () => {
    const randomCysteine = getRandomCysteine();
    if (randomCysteine) {
      setQuery(randomCysteine.primary);
      handleResultSelection(randomCysteine, "feeling_lucky", randomCysteine.primary);
    }
  };
  
  const handleResultSelection = (
    result: SearchResult,
    source: SearchSource,
    inputQuery?: string
  ) => {
    setSelectedResult(result);
    setShowSuggestions(false);
    trackCysteineSelection(source, result, inputQuery);
    
    // Extract protein ID based on result type
    if (result.type === 'protein') {
      setSelectedProtein(result.primary);
    } else if (result.type === 'cysteine') {
      // Extract protein from cysteine identifier (e.g., "Q9UIC8_C258" -> "Q9UIC8")
      const proteinId = result.primary.split('_')[0];
      setSelectedProtein(proteinId);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  // If the new protein explorer is enabled, render it instead
  if (isProteinExplorerEnabled) {
    return <ProteinExplorer />;
  }

  // Otherwise, render the original Google Home layout
  return (
    <div className="google-home">
      <div className="google-content-wrapper">
        {/* Logo */}
        <div className="logo-container">
          <div className="logo">
            <span className="logo-letter"></span>
          </div>
        </div>

        {/* Search Container */}
        <div className="A8SBwf">
          <div className={`RNNXgb ${showSuggestions ? "emcav" : ""}`}>
            <div className="SDkEP">
              <div className="iblpc">
                <div className="hvhmMe">
                  <span className="Ty0Rrc z1asCe MZy1Rb">
                    <svg
                      focusable="false"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                    >
                      <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"></path>
                    </svg>
                  </span>
                </div>
              </div>
              <div className="a4bIc">
                <textarea
                  ref={textareaRef}
                  className="gLFyf"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() =>
                    setTimeout(() => setShowSuggestions(false), 200)
                  }
                  onKeyDown={handleKeyDown}
                  autoFocus
                  title="Search"
                  aria-label="Search"
                  placeholder="Enter a Protein or Gene of interest like P04637 or TP53"
                  autoCapitalize="off"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  rows={1}
                  maxLength={2048}
                  name="q"
                  role="combobox"
                />
              </div>
              <div className="fM33ce dRYYxd">
                {query && (
                  <div className="BKRPef">
                    <div
                      className="vOY7J"
                      role="button"
                      onClick={() => setQuery("")}
                    >
                      <span className="ExCKkf z1asCe rzyADb">
                        <svg
                          focusable="false"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                        >
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
                        </svg>
                      </span>
                    </div>
                    <span className="ACRAdd"></span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Suggestions dropdown */}
          {showSuggestions && searchResults.length > 0 && (
            <div className="UUbT9 EyBRub">
              <div className="suggestions-list">
                {searchResults.map((result, index) => (
                  <div
                    key={`${result.type}-${result.primary}-${index}`}
                    className="suggestion-item"
                    onClick={() => {
                      setQuery(result.primary);
                      handleResultSelection(result, "suggestion_click", query);
                    }}
                  >
                    <div className="suggestion-content">
                      <div className="suggestion-primary">
                        <span className={`result-badge ${result.type}`}>
                          {result.type === "cysteine" ? "C" : "P"}
                        </span>
                        <span className="primary-text">{result.primary}</span>
                      </div>
                      <div className="suggestion-secondary">
                        <span className="secondary-text">
                          {result.secondary}
                        </span>
                        {result.tertiary && (
                          <span className="tertiary-text">
                            {" "}
                            • {result.tertiary}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Search Buttons */}
          <div className="lJ9FBc">
            <center>
              <input
                className="gNO89b"
                value="Search"
                aria-label="Google Search"
                name="btnK"
                role="button"
                type="submit"
                onClick={handleSearch}
              />
              <input
                className="RNmpXc"
                value="I'm Feeling Lucky"
                aria-label="I'm Feeling Lucky"
                name="btnI"
                type="submit"
                onClick={handleFeelingLucky}
              />
            </center>
          </div>
        </div>
      </div>
      
      {/* Cysteine Table */}
      {selectedProtein && (
        <CysteineTable
          proteinId={selectedProtein}
          onClose={() => setSelectedProtein(null)}
        />
      )}
    </div>
  );
};

export default GoogleHome;
